import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { withBuScope } from '@/lib/business-unit-scope';
import { BrandAdapter } from '@/adapters';
import type {
  ApiResponse,
  AutomationGuards,
  BrandSettings,
  BrandSettingsResponseDto,
  TicketFieldValue,
  UpdateAutomationThresholdDto,
  UpdateBrandSettingsDto
} from '@/types';

/** `{ bu_id }` for the selected brand, or `{}` for the account as a whole. */
type BuScope = { bu_id?: string };

interface SpamIntentsConfig {
  blockedIntents: string[];
  spamAutoCloseIntents: string[];
}

/**
 * Every read and write here takes the operator's current brand.
 *
 * Reads send `bu_id` as a param; writes append it to the URL with `withBuScope`, because
 * the body is already the payload. Both spellings reach the same backend parameter, and an
 * ABSENT one means account-wide — which is what an account with one brand always sends, so
 * nothing changes for it.
 */
export const brandService = {
  async getSettings(scope?: BuScope): Promise<BrandSettings> {
    const res = await api.get<ApiResponse<BrandSettingsResponseDto>>(
      API_ENDPOINTS.BRAND.SETTINGS,
      { params: scope }
    );
    return BrandAdapter.fromSettingsResponse(res.data.data);
  },

  /** Replaces the brand's automation guards (needs edit on Automation). Read them with `getSettings`. */
  async setAutomationGuards(automationGuards: AutomationGuards, scope?: BuScope): Promise<AutomationGuards> {
    const res = await api.put<ApiResponse<{ automationGuards: AutomationGuards }>>(
      withBuScope(API_ENDPOINTS.BRAND.AUTOMATION_GUARDS, scope ?? {}),
      { automationGuards }
    );
    return res.data.data.automationGuards;
  },

  async updateSettings(data: UpdateBrandSettingsDto, scope?: BuScope): Promise<BrandSettings> {
    const payload = BrandAdapter.toUpdateSettingsRequest(data);
    const res = await api.patch<ApiResponse<BrandSettingsResponseDto>>(
      withBuScope(API_ENDPOINTS.BRAND.SETTINGS, scope ?? {}),
      payload
    );
    return BrandAdapter.fromSettingsResponse(res.data.data);
  },

  async updateAutomationThreshold(
    data: UpdateAutomationThresholdDto,
    scope?: BuScope
  ): Promise<BrandSettings> {
    const payload = BrandAdapter.toUpdateAutomationThresholdRequest(data);
    const res = await api.patch<ApiResponse<BrandSettingsResponseDto>>(
      withBuScope(API_ENDPOINTS.BRAND.AUTOMATION_THRESHOLD, scope ?? {}),
      payload
    );
    return BrandAdapter.fromSettingsResponse(res.data.data);
  },

  /**
   * The list in force for the brand: the account's blocked intents PLUS the brand's own.
   * The backend unions them rather than letting a brand shadow the account, so a super
   * admin's global block cannot be dropped by a brand that keeps its own list.
   */
  async getBlockedIntents(scope?: BuScope): Promise<SpamIntentsConfig> {
    const res = await api.get<ApiResponse<SpamIntentsConfig>>(
      API_ENDPOINTS.BRAND.BLOCKED_INTENTS,
      { params: scope }
    );
    return {
      blockedIntents: res.data.data.blockedIntents ?? [],
      spamAutoCloseIntents: res.data.data.spamAutoCloseIntents ?? [],
    };
  },

  async setBlockedIntents(
    payload: { intentIds: string[]; autoCloseIntentIds: string[] },
    scope?: BuScope
  ): Promise<SpamIntentsConfig> {
    const res = await api.put<ApiResponse<SpamIntentsConfig>>(
      withBuScope(API_ENDPOINTS.BRAND.BLOCKED_INTENTS, scope ?? {}),
      { intentIds: payload.intentIds, autoCloseIntentIds: payload.autoCloseIntentIds }
    );
    return {
      blockedIntents: res.data.data.blockedIntents ?? [],
      spamAutoCloseIntents: res.data.data.spamAutoCloseIntents ?? [],
    };
  },

  /**
   * The helpdesk ticket fields each spam intent's auto-close fills in, keyed by intent id.
   * Account-wide, like the helpdesk itself, so no brand is sent.
   */
  async getSpamTicketFields(): Promise<Record<string, TicketFieldValue[]>> {
    const res = await api.get<ApiResponse<{ ticketFields: Record<string, TicketFieldValue[]> }>>(
      API_ENDPOINTS.BRAND.SPAM_TICKET_FIELDS
    );
    return res.data.data.ticketFields ?? {};
  },

  /** Replace one intent's values; an empty list clears them. */
  async setSpamTicketFields(intentId: string, ticketFields: TicketFieldValue[]): Promise<TicketFieldValue[]> {
    const res = await api.put<ApiResponse<{ ticketFields: TicketFieldValue[] }>>(
      API_ENDPOINTS.BRAND.SPAM_TICKET_FIELDS,
      { intentId, ticketFields }
    );
    return res.data.data.ticketFields ?? [];
  }
};
